import { blobToFile } from '@connected/ui/lib/utils';
import React, { createContext, Suspense, use } from 'react';

import type { MediaFile, MediaFileId } from '../../database/schema';
import type { DirectoryHandleAlternative } from '../../lib/opfs-alternative';

import {
    createMediaFile,
    deleteAllMediaFiles,
    deleteMediaFile,
    getMediaFile,
    updateMediaFile,
} from '../../database/models/media-file';
import { getFileSystemRoot } from '../../lib/utils';

export type MediaManager = {
    upload: (file: File, mediaFileId: MediaFileId) => Promise<MediaFile>;
    download: (mediaFile: MediaFile) => Promise<File>;
    update: (mediaFileId: MediaFileId, mediaFile: Partial<MediaFile>) => Promise<void>;
    remove: (mediaFile: MediaFile) => Promise<void>;
    removeAll: () => Promise<void>;
    fileExists: (mediaFileId: MediaFileId) => Promise<boolean>;
    getObjectURL: (mediaFile?: MediaFile) => Promise<string | null>;
    getMediaFile: (mediaFileId: MediaFileId) => Promise<MediaFile | undefined>;
};

function createMediaManager(opfsRoot: FileSystemDirectoryHandle | DirectoryHandleAlternative): MediaManager {
    // Helper to ensure the /media directory exists
    const getMediaRootDir = () => opfsRoot.getDirectoryHandle('media', { create: true });

    const upload = async (file: File, mediaFileId: MediaFileId): Promise<MediaFile> => {
        const mediaHandle = await getMediaRootDir();
        const mediaFileHandle = await mediaHandle.getFileHandle(mediaFileId, { create: true });
        const writable = await mediaFileHandle.createWritable();
        await writable.write(file);
        await writable.close();

        const mediaFile = await createMediaFile({
            mediaFileId,
            name: file.name,
            size: file.size,
            mimeType: file.type,
            opfsPath: mediaFileHandle.name,
        });

        return mediaFile;
    };

    const download = async (mediaFile: MediaFile): Promise<File> => {
        if (!mediaFile.opfsPath) {
            throw new Error(`Media file ${mediaFile.mediaFileId} has no opfsPath`);
        }

        const mediaHandle = await getMediaRootDir();
        const mediaFileHandle = await mediaHandle.getFileHandle(mediaFile.opfsPath);

        const blob = await mediaFileHandle.getFile();

        return blobToFile(blob, mediaFile.name, mediaFile.mimeType);
    };

    const update = async (mediaFileId: MediaFileId, mediaFile: Partial<MediaFile>): Promise<void> => {
        await updateMediaFile(mediaFileId, mediaFile);
    };

    const remove = async (mediaFile: MediaFile): Promise<void> => {
        await deleteMediaFile(mediaFile.mediaFileId);

        if (mediaFile.opfsPath) {
            try {
                const mediaHandle = await getMediaRootDir();
                await mediaHandle.removeEntry(mediaFile.opfsPath);
            }
            catch (e) {
                console.error(`Failed to remove file from OPFS: ${mediaFile.opfsPath}`, e);
            }
        }
    };

    const removeAll = async (): Promise<void> => {
        await deleteAllMediaFiles();

        try {
            await opfsRoot.removeEntry('media', { recursive: true });
        }
        catch (e) {
            if (e instanceof Error && e.name !== 'NotFoundError') {
                console.error('Failed to remove media directory from OPFS', e);
                return;
            }
            console.error('Failed to remove media directory from OPFS', e);
        }
    };

    const fileExists = async (mediaFileId: MediaFileId): Promise<boolean> => {
        const mediaFile = await getMediaFile(mediaFileId);
        return !!mediaFile;
    };

    /**
     * @param mediaFile - The media file to get the object URL for.
     * @returns The object URL for the media file.
     * @note This is a temporary solution be aware that it will leak memory.
     */
    const getObjectURL = async (mediaFile?: MediaFile): Promise<string | null> => {
        if (!mediaFile)
            return null;
        const file = await download(mediaFile);
        return URL.createObjectURL(file);
    };

    return {
        upload,
        download,
        update,
        remove,
        removeAll,
        getObjectURL,
        getMediaFile,
        fileExists,
    };
}

export const MediaManagerContext = createContext<MediaManager | null>(null);

let initPromise: Promise<MediaManager> | null = null;

async function initializeMediaManager(): Promise<MediaManager> {
    const opfsRoot = await getFileSystemRoot();
    return createMediaManager(opfsRoot);
}

export function getInitPromise(): Promise<MediaManager> {
    if (!initPromise) {
        initPromise = initializeMediaManager();
    }
    return initPromise;
}

export type MediaManagerProviderProps = {
    children: React.ReactNode;
};

function MediaManagerInnerProvider({
    children,
}: MediaManagerProviderProps) {
    const manager = use(getInitPromise());

    return (
        <MediaManagerContext value={manager}>
            {children}
        </MediaManagerContext>
    );
}

export function MediaManagerProvider({
    fallback,
    ...props
}: MediaManagerProviderProps & {
    fallback: React.ReactNode;
}) {
    return (
        <Suspense fallback={fallback}>
            <MediaManagerInnerProvider {...props} />
        </Suspense>
    );
}
